import { newrelic, runGoogleReCaptcha } from "@jti/fe-clients";
import type { Config } from "@jti/fe-common";
import { getConfig, isCustomErrorCheck, isServerSide } from "@jti/fe-common";

import { ConnectionError, getIsEvo, initCookieSync } from "../utils";

import { RestLocalCache } from "./RestLocalCache";

const reCaptchaTokenKey = "X-DES-RECAPTCHA-TOKEN";
const reCaptchaFeatureToggle = "reCaptchaEnabled";
const reCaptchaId = "X-RECAPTCHA-ID";
const xBrandHeader = "X-BRAND";
const xDesOrgBrandHeader = "X-DES-ORG-BRAND";

export type RestResponseType<Data = unknown> = Data extends unknown[]
  ? { data: Data; statusCode: number }
  : Data & { statusCode: number };

export class Rest {
  name = "RestApi";
  private commonHeaders: { [key: string]: string };
  private config: RequestInit;
  private baseUrl: string;
  private static locale?: string;
  private cacheInstance: RestLocalCache;
  protected readonly injectedConfig?: Config;
  public readonly isEvo: boolean = false;

  constructor(baseUrl = "/", config?: Config, locale?: string) {
    this.commonHeaders = {
      "content-type": "application/json",
    };
    this.baseUrl = baseUrl;
    this.config = {
      method: "GET",
    };
    this.injectedConfig = config;
    this.cacheInstance = RestLocalCache.getInstance();
    this.isEvo = getIsEvo(baseUrl);
    this.initLocale(locale);
    this.initCookieStore(this.isEvo);
    this.initBrandHeader();
  }

  setCommonHeader(key: string, value: string): void {
    this.commonHeaders[key] = value;
  }

  /**
   * FIXME: at this moment the AbortSignal.timeout() is not fully supported by all of the green-browsers
   *  and current typescript version used in project, so we need to use "the ogly way" instead;
   * mr. FutureDeveloper, please fix this when you have time
   *
   * @param timeout in milliseconds
   * @returns AbortSignal
   */
  getTimeoutSignal(timeout: number): AbortSignal {
    const controller: AbortController = new AbortController();

    setTimeout(() => controller.abort(), timeout || 5000);

    return controller.signal;
  }

  getConfigKeyValue(config: Config, toggleKey: keyof Config) {
    const value = config[toggleKey];

    if (typeof value === "boolean") {
      return value;
    }
    return value === "true";
  }

  async request<Type, R = RestResponseType<Type>>(
    url: string,
    init: RequestInit = {},
    useRecaptcha?: boolean,
    useLocalCache?: boolean,
    timeout?: number
  ): Promise<R> {
    const currentUrl = `${this.baseUrl}${url}`;
    const isLocalCacheEnabled = useLocalCache && typeof window !== "undefined";

    const promiseCallback = (resolve: (data: R | PromiseLike<R>) => void, reject: (data?: unknown) => void) => {
      const config = this.injectedConfig ?? getConfig();
      const isReCaptchaEnabled =
        this.getConfigKeyValue(config, reCaptchaFeatureToggle) ||
        this.getConfigKeyValue(config, "X-RECAPTCHA-FEATURE-TOGGLE");

      const token = config[reCaptchaId];

      const fetchCall = (tokenString = "") => {
        const headers: HeadersInit = {
          ...this.commonHeaders,
          ...init.headers,
        };

        if (isReCaptchaEnabled && useRecaptcha) {
          Object.assign(headers, {
            [reCaptchaTokenKey]: tokenString,
          });
        }

        if (timeout) {
          this.config.signal = this.getTimeoutSignal(timeout);
        }

        fetch(currentUrl, {
          ...this.config,
          ...init,
          headers,
        })
          .then(async (response) => {
            if (response.status === 204) {
              return resolve({ statusCode: response.status } as R);
            }

            const data = await response.json();
            const responseData: R = Array.isArray(data)
              ? { data, statusCode: response.status }
              : { ...data, statusCode: response.status };

            if (response.status >= 200 && response.status < 300) {
              if (isLocalCacheEnabled) {
                this.cacheInstance.setValue(currentUrl, responseData);
              }

              return resolve(responseData);
            }

            if (isLocalCacheEnabled) {
              this.cacheInstance.unregisterAction(currentUrl);
            }

            return reject(responseData);
          })
          .catch(() => {
            if (isLocalCacheEnabled) {
              this.cacheInstance.unregisterAction(currentUrl);
            }

            return reject();
          });
      };

      if (isReCaptchaEnabled && useRecaptcha) {
        runGoogleReCaptcha(token, fetchCall);
      } else {
        fetchCall();
      }
    };

    const handleError = (error: unknown) => {
      const isCustomError = isCustomErrorCheck(error);

      const message = isCustomError ? (error.message ?? `${this.name}ConnectionError`) : `${this.name}ConnectionError`;
      const statusCode = isCustomError ? (error.statusCode ?? 0) : 0;

      newrelic.noticeError(new ConnectionError(message, this.baseUrl, url, statusCode), {
        response: JSON.stringify(error),
        url,
        baseUrl: this.baseUrl,
      });

      if (isCustomError && this.isEvo) {
        // This sets error_code (only if not already present), so getErrorMessageFromError enters the error_code branch and translates via translateBackendErrorCode(error_code, fallback, dictionary). The original server message is preserved for logging/debugging.
        (error as Record<string, unknown>).error_code ??= "evo.fallback.error";
        throw error;
      }

      throw error;
    };

    if (isLocalCacheEnabled) {
      if (this.cacheInstance.hasValue(currentUrl)) {
        return await this.cacheInstance.getValue<R>(currentUrl, promiseCallback).catch(handleError);
      }

      this.cacheInstance.registerAction(currentUrl);
    }

    return new Promise<R>(promiseCallback).catch(handleError);
  }

  protected initLocale(locale?: string): void {
    const resolvedLocale =
      locale ??
      Rest.locale ??
      (typeof document !== "undefined" ? document.querySelector("html")?.getAttribute("lang") || undefined : undefined);

    if (!Rest.locale && resolvedLocale) {
      Rest.locale = resolvedLocale;
    }
    if (resolvedLocale) {
      this.setCommonHeader("x-des-locale", resolvedLocale);
    }
  }

  protected initCookieStore(isEvo: boolean): void {
    if (isEvo && !isServerSide()) {
      initCookieSync();
    }
  }

  protected initBrandHeader(): void {
    try {
      const brand = process.env.NEXT_PUBLIC_BRAND;
      if (brand) {
        this.setCommonHeader(xBrandHeader, brand);
        this.setCommonHeader(xDesOrgBrandHeader, brand);
      }
    } catch {
      // process or process.env not available (e.g. non-Node environment)
    }
  }
}
